import React, {useEffect, useState} from "react";
import "antd/dist/antd.css";
import "./WorklistPage.scss";
import {useHistory} from 'react-router-dom';
import {ExamApiService} from "../../api/ExamApi.service";
import {ExamImagesApiService} from "../../api/ExamImagesApi.service";
import {useSelector, useDispatch} from 'react-redux'
import {update} from "../../reducers/paginationSlice";
import {updateSort} from "../../reducers/sortSlice";
import Grid from "../../components/Grid/Grid";
import {Tag, Button, Divider, Input, message} from 'antd';
import Moment from 'react-moment';
import {Diagnosis} from '../../components/Diagnosis/Diagnosis';
import GuardedRenderer from "../../components/GuardedRenderer/GuardedRenderer";
import {useTranslation} from "react-i18next";

export default function WorklistPage() {
    const {t} = useTranslation();
    const pagination = useSelector((x) => x.worklist.pagination['SPAIN_GRID_FEATURE']);
    const sort = useSelector((x) => x.worklist.sort['SPAIN_GRID_FEATURE']);
    const [examResendPending, setExamResendPending] = useState(false);
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false);
    const [exams, setExams] = useState([]);
    const [searchParams] = useState([]);
    let history = useHistory();

    const [searchText, setSearchText] = useState('');

    const handleSearchFieldChange = (e) => {
        setSearchText(e.target.value);
        searchParams['filterValue'] = e.target.value;
    };

    const handleReset = clearFilters => {
        setSearchText('');
		searchParams['filterValue'] = null;
		onSearch();
	};

    const onSearch = () => {
        loadExams(
                pagination.current,
                sort.column,
                sort.order,
                searchParams)
    };

    const examStates = {
        'ANALYZED': {
            title: t('list.new'),
            color: 'green'
        },
        'ANALYSING': {
            title: t('list.pending'),
            color: 'orange'
        },
        'FAILED': {
            title: t('list.failed'),
            color: 'red'
        },
        'PROCESSED': {
            title: t('list.processed')
        },
        'ICC': {
            title: t('list.icc')
        },
        'ICC_REVIEWED': {
            title: t('list.iccReviewed')
        }
    };

    const columns = [
        {
            title: t('list.status'),
            dataIndex: 'examinationStatus',
            key: 'examinationStatus',
            sorter: true,
            sortOrder: undefined,
            render: status => {
                return examStates[status] ? <Tag color={examStates[status].color}>
                    {examStates[status].title.toUpperCase()}
                </Tag> : <Tag>
                    {status}
                </Tag>
            },
            width: 150
        },
        {
            title: t('list.examDate'),
            dataIndex: 'examinationDate',
            sorter: true,
            sortOrder: undefined,
            render: date => (
                <Moment format="DD-MM-yyyy HH:mm">
                    {date}
                </Moment>
            ),
            width: 200
        },
        {
            title: t('list.patientNumber'),
            dataIndex: 'patientNumber',
            width: 150
        },
        {
            title: t('list.author'),
            render: (a, record) => {
                return <div>
                    {record.creatorUserInfo && record.creatorUserInfo.userName}
                </div>
            },
            width: 150
        },
        {
            title: t('common.department'),
            render: (a, record) => {
                return <div>
                    {record.creatorDepartment && record.creatorDepartment.name}
                </div>
            },
            width: 200
        },
        {
            title: t('list.diagnosis'),
            dataIndex: 'diagnosisleft',
            render: (a, record) => {
                function formatAdviceForClipboard() {
                    function formatClassifications(diagnosis) {
                        let text = "";
                        if (diagnosis !== undefined) {
                            text += diagnosis.maculopathyClassification != null && ("M" + diagnosis.maculopathyClassification + " ");
                            text += diagnosis.retinopathyClassification != null && ("R" + diagnosis.retinopathyClassification + " ");
                        }
                        return text;
                    }

                    let text = "";
                    text += "OS: " + formatClassifications(record.leftEyeDiagnosis) + "\n";
                    text += "OD: " + formatClassifications(record.rightEyeDiagnosis) + "\n";
                    text += "\nNotes: " + record.doctorNote + "\n";
                    text += "\nAdvice: " + record.doctorAdvice;
                    return text;
                }

                return <div>
                    <div>
                        <Diagnosis title={t('common.OS')} diagnosisSet={record.leftEyeDiagnosis}/>
                    </div>
                    <div>
                        <Diagnosis title={t('common.OD')} diagnosisSet={record.rightEyeDiagnosis}/>
                    </div>
                    <Button className="p-0" type='link'
                            onClick={(e) => {
                                e.stopPropagation();
                                console.log("copy text")
                                void navigator.clipboard.writeText(formatAdviceForClipboard());
                            }}>copy to clipboard</Button>
                </div>
            },
            width: 200
        },
        {
            title: t('list.actions'),
            render: (a, record) => renderActionsColumn(a, record),
            width: 200
        }
    ];

    if (sort) {
        const sortedColumn = columns.filter(x => x.dataIndex === sort.column)[0];

        if (sortedColumn) {
            sortedColumn.sortOrder = sort.order;
        }
    }

    useEffect(() => {
        loadExams(
            pagination.current,
            sort.column,
            sort.order,
            searchParams);
    }, []);

    const renderActionsColumn = function (a, record) {
        return (<div>
            <Button onClick={(e) => handleDownloadClick(e, `${record.examinationId}`)}
               title={t('list.downloadExplanation')}>
                {t('common.download')}
            </Button>

            {
                <GuardedRenderer requiredFeature="RESEND_EXAM">
                    {record.examinationStatus === 'FAILED' &&
                        <>
                            <Divider type="vertical"/>
                            <Button onClick={(e) => handleResendClick(e, record)}
                                    className="p-0" type='link' loading={examResendPending[record.examinationId]}>
                                {t('list.resend')}
                            </Button>
                        </>}
                </GuardedRenderer>
            }
        </div>)
    }

    const handleResendClick = function (e, record) {
        setExamResendPending({[record.examinationId]: true});
        ExamApiService.resendExam(record.examinationId)
            .then(() => {
                loadExams(
                    pagination.current,
                    sort.column,
                    sort.order,
                    searchParams);
            })
            .finally(() => {
                setExamResendPending({[record.examinationId]: false});
            });

        e.stopPropagation();
        return false;
    }

    const handleDownloadClick = (e, examinationId) => {
        e.stopPropagation();

        const hideMessage = message.loading('File downloading...');

        ExamImagesApiService.getImagesZip(examinationId)
                .then(
                    (blob) => {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        const fileName = `images${examinationId}.zip`;
                        link.href = url;
                        link.setAttribute('download', fileName);
                        document.body.appendChild(link);
                        link.click();

                        URL.revokeObjectURL(url);
                        document.body.removeChild(link);
                    }
                ).catch(() => {
                    message.error('Failed to download file');
                }).finally(() => {
                    hideMessage();
                });

        return false;
    }


    const loadExams = function (page, sortField, sortOrder, searchParams) {
        setIsLoading(true);

        if (sortOrder === undefined || sortField === undefined) {
            sortField = undefined;
            sortOrder = undefined;
        }

        return ExamApiService
            .getPaginatedExaminations(page, pagination.pageSize, sortField, sortOrder, searchParams)
            .then(
                exams => {
                    setExams(exams.data);
                    setIsLoading(false);

                    dispatch(update({
                        SPAIN_GRID_FEATURE: {
                            current: page,
                            pageSize: pagination.pageSize,
                            total: exams.totalElements,
                            position: 'both'
                        }
                    }));

                    dispatch(updateSort({
                        SPAIN_GRID_FEATURE: {
                            column: sortField,
                            order: sortOrder
                        }
                    }));
                },
                () => {
                    setIsLoading(false);
                }
            );
    }

    const handleTableChange = (pagination, filters, sorter) => {
        console.log('pagination:', pagination);
        console.log('pagination.current:', pagination.current);

        loadExams(pagination.current, sorter.columnKey, sorter.order, searchParams)
    };

    const onRowClick = function (record) {
        history.push(`./viewer/${record.examinationId}`);
    }

    return (
    <div>
        <div style={{margin: '1rem 1rem 0 1rem', display: 'flex'}}>
             <Input
                placeholder="Search in Patient Number, Author, Advice"
                value={searchText}
                onChange={handleSearchFieldChange}
                onPressEnter={() => onSearch()}
                style={{
                    border: '2px solid #d9d9d9',
                    borderRadius: '4px',
                    marginRight: '10px',
                    transition: 'border-color 0.2s ease',
                    ':focus': {
                        border: '1px solid #1890ff',
                        boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
                        outline: 'none'
                    }
                }}
             />
             <Button type="primary" style={{ width: 90 }}
                    onClick={() => onSearch()}>
                    {t('common.search')}
            </Button>
            <Button style={{ width: 90, marginLeft: '10px' }}
                    onClick={() => handleReset()}>
                    {t('common.reset')}
            </Button>
        </div>
        <Grid columns={columns} handleTableChange={handleTableChange} isLoading={isLoading}
                data={exams} pagination={pagination} onRowClick={onRowClick}></Grid>
    </div>
    );
}
